import { uppercaseConversionCapture } from '../../utils/conversionCaptureText';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { formatWorkOrderIssue, validateConversionWorkOrders } from '../../utils/conversionWorkOrderValidation';
import {
  formatPaperRollIssue,
  synchronizePaperRollRows,
  validatePaperRollSeries,
} from '../../utils/conversionPaperRollValidation';
import ConversionValidationSummary from './ConversionValidationSummary';

const toIntOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
};

// Temporary ID counter for new rows (always negative to distinguish from backend IDs)
// Backend IDs are positive auto-increment, temp IDs are negative and decrement
let tempIdCounter = -1;
const getTempId = () => {
  const id = tempIdCounter;
  tempIdCounter--;
  return id;
};

const emptyRow = () => ({
  id: getTempId(),
  item: '',
  work_order: '',
  quantity: null,
  counter: null,
  bags_good: null,
  scrap: null,
  roll_no: '',
  pallet_no: '',
  serial_or_part: '',
  fsc: false,
  details: {}
});

const sum = (arr, pick) => arr.reduce((acc, x) => acc + (toIntOrNull(pick(x)) || 0), 0);

const ItemCell = ({ value, siteId, disabled, onChange, onPickMeta, metadata }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const boxRef = useRef(null);
  const dropdownRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => setQuery(value || ''), [value]);

  // Build tooltip text from metadata
  const tooltipText = metadata && (metadata.name || metadata.bag_style || metadata.bag_size_group || metadata.packing_group)
    ? [metadata.name, metadata.bag_style, metadata.bag_size_group, metadata.packing_group]
      .filter(Boolean)
      .join(' · ')
    : '';

  // Calculate dropdown position when opened
  useEffect(() => {
    if (open && boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [open]);

  const fetchOptions = useCallback(
    async (q) => {
      const trimmed = (q || '').trim();
      if (!trimmed || trimmed.length < 2) {
        setOptions([]);
        return;
      }
      try {
        setLoading(true);
        const params = { search: trimmed, per_page: 10, page: 1 };
        if (siteId) params.site_id = siteId;
        const { data } = await axios.get('/orders-data', { params });
        setOptions(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [siteId]
  );

  const onInput = (input) => {
    const v = input.toUpperCase();
    setQuery(v);
    onChange?.(v);
    setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchOptions(v), 250);
  };

  useEffect(() => {
    const onDoc = (e) => {
      const target = e.target;
      const clickedInInputBox = boxRef.current?.contains(target);
      const clickedInDropdown = dropdownRef.current?.contains(target);
      if (!clickedInInputBox && !clickedInDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (it) => {
    const next = String(it?.item || '').toUpperCase();
    setQuery(next);
    setOpen(false);
    if (onPickMeta) {
      onPickMeta(it);
    } else {
      onChange?.(next);
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => onInput(e.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && open) {
            const exactMatch = options.find((option) => String(option.item).toLowerCase() === query.trim().toLowerCase());
            if (exactMatch) pick(exactMatch);
            else setOpen(false);
          }
        }}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        placeholder="Buscar item..."
        title={tooltipText}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 font-mono"
      />
      {open && !disabled && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${Math.max(dropdownPosition.width, 350)}px`,
            zIndex: 9999,
            maxHeight: '250px'
          }}
        >
          <div className="px-3 py-2 text-xs text-slate-500 border-b border-slate-100 flex items-center justify-between">
            <span>{loading ? 'Buscando...' : options.length > 0 ? 'Sugerencias' : 'Sin resultados'}</span>
            <span className="text-[11px]">Enter: siguiente campo</span>
          </div>
          <div className="max-h-56 overflow-auto">
            {options.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => pick(it)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                <div className="font-semibold text-slate-900">{it.item}</div>
                <div className="text-xs text-slate-600 truncate">
                  {it.name} · {it.bag_style} · {it.bag_size_group} · {it.packing_group}
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const ConversionProductionGrid = ({ rows, siteId, readOnly, onChange }) => {
  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const setRow = (rowId, patch) => {
    const next = list.map((r) => (r.id === rowId ? { ...r, ...uppercaseConversionCapture(patch) } : r));
    onChange?.(next);
  };

  const setPaperRollField = (rowId, field, value) => {
    onChange?.(synchronizePaperRollRows(list, rowId, field, uppercaseConversionCapture({ [field]: value })[field]));
  };

  const addRow = () => onChange?.([...list, emptyRow()]);

  const duplicateRow = (row) => {
    const currentPalletNo = toIntOrNull(row.pallet_no);
    const nextPalletNo = currentPalletNo ? currentPalletNo + 1 : null;

    const copy = {
      ...emptyRow(),
      work_order: row.work_order,
      item: row.item,
      quantity: row.quantity,
      serial_or_part: row.serial_or_part,
      fsc: row.fsc,
      roll_no: row.roll_no,
      pallet_no: nextPalletNo ? String(nextPalletNo) : '',
      details: row.details || {}
    };
    onChange?.([...list, copy]);
  };

  const deleteRow = (row) => {
    if (!window.confirm('¿Borrar este renglón?')) return;
    onChange?.(list.filter((r) => r.id !== row.id));
  };

  const totals = useMemo(() => {
    return {
      quantity: sum(list, (r) => r.quantity),
      counter: sum(list, (r) => r.counter),
      good: sum(list, (r) => r.bags_good),
      scrap: sum(list, (r) => r.scrap),
    };
  }, [list]);

  const accumulatedVariancePct = useMemo(() => {
    if (!totals.counter) return 0;
    return (totals.scrap / totals.counter) * 100;
  }, [totals]);

  const workOrderValidation = useMemo(() => validateConversionWorkOrders(list), [list]);
  const invalidWorkOrders = useMemo(
    () => new Set(workOrderValidation.workOrderIssues.map((issue) => issue.workOrder)),
    [workOrderValidation]
  );
  const paperRollValidation = useMemo(() => validatePaperRollSeries(list), [list]);
  const paperRollConflictIssues = paperRollValidation.issues.filter((issue) => !['roll_missing_series', 'missing_roll'].includes(issue.type));
  const missingPaperSeriesIssues = paperRollValidation.issues.filter((issue) => issue.type === 'roll_missing_series');
  const missingRollIssues = paperRollValidation.issues.filter((issue) => issue.type === 'missing_roll');
  const validationGroups = useMemo(() => {
    const groups = [];

    if (workOrderValidation.workOrderIssues.length > 0) {
      groups.push({
        key: 'work-order-balance',
        title: 'La producción no cuadra por Work Order',
        messages: workOrderValidation.workOrderIssues.map(formatWorkOrderIssue),
      });
    }

    if (workOrderValidation.missingWorkOrderRows.length > 0) {
      groups.push({
        key: 'missing-work-order',
        title: 'Work Order pendiente',
        messages: [`Completa el Work Order antes de cerrar en ${workOrderValidation.missingWorkOrderRows.length === 1 ? 'el renglón' : 'los renglones'} ${workOrderValidation.missingWorkOrderRows.join(', ')}.`],
      });
    }

    if (workOrderValidation.incompleteItemRows.length > 0) {
      groups.push({
        key: 'incomplete-item-rows',
        title: 'Renglón de producción por completar',
        messages: [`Puedes seguir capturando; antes de cerrar agrega datos de producción o elimina ${workOrderValidation.incompleteItemRows.length === 1 ? 'el renglón' : 'los renglones'} ${workOrderValidation.incompleteItemRows.join(', ')}.`],
      });
    }

    if (paperRollConflictIssues.length > 0) {
      groups.push({
        key: 'paper-roll-conflicts',
        title: 'Revisa la relación entre Rollo y Serie de Papel',
        messages: paperRollConflictIssues.map(formatPaperRollIssue),
      });
    }

    if (missingPaperSeriesIssues.length > 0) {
      groups.push({
        key: 'missing-paper-series',
        title: 'Serie de Papel pendiente',
        messages: missingPaperSeriesIssues.map(formatPaperRollIssue),
      });
    }

    if (missingRollIssues.length > 0) {
      groups.push({
        key: 'missing-roll',
        title: 'Número de rollo pendiente',
        messages: missingRollIssues.map(formatPaperRollIssue),
      });
    }

    return groups;
  }, [missingPaperSeriesIssues, missingRollIssues, paperRollConflictIssues, workOrderValidation]);

  const hasWarnings = (row) => {
    const hasProductionData = [row?.quantity, row?.counter, row?.bags_good, row?.scrap]
      .some((value) => value !== '' && value != null);
    return hasProductionData && !String(row?.work_order ?? '').trim();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={addRow}
          disabled={readOnly}
          className="px-3 py-2 rounded-lg bg-blue-700 text-white font-semibold shadow hover:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
        >
          + Agregar renglón
        </button>
      </div>

      <ConversionValidationSummary groups={validationGroups} />

      <div className="rounded-xl border border-slate-200 overflow-visible">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full text-xs" style={{ overflow: 'visible' }}>
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="px-2 py-2 text-left font-bold sticky left-0 bg-slate-50 z-10 border-r border-slate-200">#</th>
                <th className="px-2 py-2 text-left font-bold w-[110px]">Item</th>
                <th className="px-2 py-2 text-left font-bold">Work Order</th>
                <th className="px-2 py-2 text-left font-bold">#Tarima</th>
                <th className="px-2 py-2 text-left font-bold">#Rollo</th>
                <th className="px-2 py-2 text-right font-bold">Cantidad</th>
                <th className="px-2 py-2 text-right font-bold">Contador</th>
                <th className="px-2 py-2 text-right font-bold">Bolsas buenas</th>
                <th className="px-2 py-2 text-right font-bold">Scrap</th>
                <th className="px-2 py-2 text-left font-bold">Serie de Papel</th>
                <th className="px-2 py-2 text-center font-bold">FSC</th>
                <th className="px-2 py-2 text-center font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody style={{ overflow: 'visible' }}>
              {list.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-slate-500">
                    Sin renglones todavía. Usa <span className="font-semibold">+ Agregar renglón</span>.
                  </td>
                </tr>
              ) : (
                list.map((row, idx) => {
                  const warn = hasWarnings(row);
                  const invalidWorkOrder = invalidWorkOrders.has(String(row.work_order ?? '').trim().toUpperCase());
                  const missingPaperSeries = paperRollValidation.missingSeriesRowIds.has(row.id);
                  const missingRoll = paperRollValidation.missingRollRowIds.has(row.id);
                  const conflictingPaperSeries = paperRollValidation.warningRowIds.has(row.id) && !missingPaperSeries;
                  const meta = row.details || {};
                  return (
                    <tr key={row.id} className={`border-t ${invalidWorkOrder ? 'bg-red-50/70' : warn ? 'bg-amber-50/40' : 'bg-white'} hover:bg-slate-50`} style={{ overflow: 'visible' }}>
                      <td className="px-3 py-2 sticky left-0 bg-inherit z-10 border-r border-slate-200">
                        <span className="font-bold text-slate-900">{idx + 1}</span>
                      </td>
                      <td className="px-2 py-2 min-w-[110px] max-w-[110px]" style={{ overflow: 'visible' }}>
                        <ItemCell
                          value={row.item}
                          siteId={siteId}
                          disabled={readOnly}
                          metadata={meta}
                          onChange={(v) => setRow(row.id, { item: v })}
                          onPickMeta={(it) => {
                            const item = it?.item || row.item;
                            setRow(row.id, {
                              item,
                              details: {
                                ...(row.details || {}),
                                item,
                                name: it?.name || '',
                                bag_size_group: it?.bag_size_group || '',
                                bag_style: it?.bag_style || '',
                                packing_group: it?.packing_group || '',
                                lbs_factor: it?.lbs_factor ?? '',
                              }
                            });
                          }}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={row.work_order ?? ''}
                          onChange={(e) => setPaperRollField(row.id, 'work_order', e.target.value)}
                          disabled={readOnly}
                          placeholder="WO / Scanner..."
                          className="w-full min-w-[110px] rounded-lg border border-slate-300 px-2 py-1.5 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 font-mono text-sm font-semibold"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={row.pallet_no ?? ''}
                          onChange={(e) => setRow(row.id, { pallet_no: e.target.value })}
                          disabled={readOnly}
                          className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={row.roll_no ?? ''}
                          onChange={(e) => setPaperRollField(row.id, 'roll_no', e.target.value)}
                          aria-label={`Rollo del renglón ${idx + 1}`}
                          disabled={readOnly}
                          className={`w-20 rounded-lg border px-2 py-1.5 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 ${conflictingPaperSeries
                            ? 'border-red-400 bg-red-50 focus:ring-red-500'
                            : missingPaperSeries || missingRoll
                              ? 'border-amber-400 bg-amber-50 focus:ring-amber-500'
                            : 'border-slate-300'
                            }`}
                          title={conflictingPaperSeries ? 'Serie de Papel duplicada' : missingPaperSeries ? 'Falta Serie de Papel' : missingRoll ? 'Falta número de rollo' : ''}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={row.quantity ?? ''}
                          onChange={(e) => setRow(row.id, { quantity: toIntOrNull(e.target.value) })}
                          disabled={readOnly}
                          className="w-20 ml-auto block rounded-lg border border-slate-300 px-2 py-1.5 text-right text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                          min={0}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={row.counter ?? ''}
                          onChange={(e) => {
                            const counter = toIntOrNull(e.target.value);
                            const good = toIntOrNull(row.bags_good);
                            const scrap = (counter && good) ? counter - good : null;
                            setRow(row.id, { counter, scrap });
                          }}
                          disabled={readOnly}
                          className="w-24 ml-auto block rounded-lg border border-slate-300 px-2 py-1.5 text-right text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                          min={0}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={row.bags_good ?? ''}
                          onChange={(e) => {
                            const good = toIntOrNull(e.target.value);
                            const counter = toIntOrNull(row.counter);
                            const scrap = (counter && good) ? counter - good : null;
                            setRow(row.id, { bags_good: good, scrap });
                          }}
                          disabled={readOnly}
                          className="w-28 ml-auto block rounded-lg border border-slate-300 px-2 py-1.5 text-right text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                          min={0}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <div className="w-20 ml-auto flex items-center">
                          <input
                            type="number"
                            value={row.scrap ?? ''}
                            onChange={(e) => setRow(row.id, { scrap: toIntOrNull(e.target.value) })}
                            disabled={readOnly}
                            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-right text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 bg-slate-50"
                            min={0}
                            title="Calculado automáticamente: Contador - Bolsas buenas"
                          />
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={row.serial_or_part ?? ''}
                          onChange={(e) => setPaperRollField(row.id, 'serial_or_part', e.target.value)}
                          aria-label={`Serie de Papel del renglón ${idx + 1}`}
                          disabled={readOnly}
                          placeholder="Serie de Papel"
                          className={`w-28 rounded-lg border px-2 py-1.5 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 ${conflictingPaperSeries
                            ? 'border-red-400 bg-red-50 focus:ring-red-500'
                            : missingPaperSeries
                              ? 'border-amber-400 bg-amber-50 focus:ring-amber-500'
                            : 'border-slate-300'
                          }`}
                          title={conflictingPaperSeries ? 'Serie de Papel duplicada' : missingPaperSeries ? 'Completar antes de cerrar' : ''}
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={Boolean(row.fsc)}
                          onChange={(e) => setRow(row.id, { fsc: e.target.checked })}
                          disabled={readOnly}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => duplicateRow(row)}
                            disabled={readOnly}
                            title="Duplicar"
                            className="p-2 rounded-lg bg-white border border-slate-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteRow(row)}
                            disabled={readOnly}
                            title="Borrar"
                            className="p-2 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <td className="px-3 py-2 font-bold sticky left-0 bg-slate-50 z-10 border-r border-slate-200" colSpan={5}>Totales</td>
                <td className="px-2 py-2 text-right font-extrabold text-slate-900">{totals.quantity.toLocaleString()}</td>
                <td className="px-2 py-2 text-right font-extrabold text-slate-900">{totals.counter.toLocaleString()}</td>
                <td className="px-2 py-2 text-right font-extrabold text-slate-900">{totals.good.toLocaleString()}</td>
                <td className="px-2 py-2 text-right font-extrabold text-slate-900">{totals.scrap.toLocaleString()}</td>
                <td className="px-2 py-2 text-right" colSpan={3}>
                  <div className="text-[11px] font-semibold text-slate-600">Varianza acumulada</div>
                  <div className="text-sm font-extrabold text-slate-900">{accumulatedVariancePct.toFixed(2)}%</div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConversionProductionGrid;
